// "use client"

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { authService, AuthResponse, ApiResponse } from '../services/authService';
import { getAuthToken, setAuthToken, getUserData, setUserData, clearAuthData, setStudentData, clearStudentData, getTenantSchema, setTenantSchema } from 'utils/storage';
import { useSchoolIdForAdmin } from '../hooks/useSchoolIdForAdmin';
import { enableForceLogoutGuard, disableForceLogoutGuard } from 'utils/forceLogoutGuard';

interface User {
  id: number;
  username: string;
  email: string;
  schoolId: number | null;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  wasAutoLoggedOut: boolean;
  login: (credentials: { email: string; password: string }) => Promise<User>;
  register: (userData: { username: string; password: string; email: string; captcha?: string }) => Promise<User>;
  forgotPassword: (email: string) => Promise<string>;
  verifyOtp: (email: string, otp: number) => Promise<string>;
  resetPassword: (email: string, newPassword: string) => Promise<string>;
  logout: () => void;
  clearAutoLogoutFlag: () => void;
  isAdmin: () => boolean;
  isSchoolAdmin: () => boolean;
  isTeacher: () => boolean;
  isStudent: () => boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [wasAutoLoggedOut, setWasAutoLoggedOut] = useState(false);
  const queryClient = useQueryClient();

  const logout = useCallback(() => {
    // Clear all authentication data from both sessionStorage and localStorage
    clearAuthData();
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Tenant-ID'];
    setUser(null);
  }, []);

  const clearAutoLogoutFlag = () => {
    setWasAutoLoggedOut(false);
  };

  useEffect(() => {
    // Get token and user from storage (sessionStorage preferred, clears when tab closes)
    const token = getAuthToken();
    const userString = getUserData();
    if (token && userString) {
      const userData = JSON.parse(userString);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData); // schoolId is preserved here
    }
    setIsLoading(false);
  }, []);

  // Setup axios interceptor for JWT expiration handling
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // JWT expired or invalid - auto logout
          setWasAutoLoggedOut(true);
          logout();
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [logout]);

  const login = async (credentials: { email: string; password: string }) => {
    try {
      const response = await authService.login(credentials);
      // Remove both 'token' and duplicate 'role' field
      const { token, role, ...userData } = response.data;

      // Clear any previous student data to avoid showing stale names from past logins
      clearStudentData();

      // Store token and user data (uses sessionStorage - clears when tab closes)
      setAuthToken(token);
      setUserData(JSON.stringify(userData));

      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set tenant header if available in storage
      const tenantSchema = getTenantSchema();
      let schoolData = null;
      
      if (tenantSchema) {
        axios.defaults.headers.common['X-Tenant-ID'] = tenantSchema;
      } else if (userData.schoolId) {
        // If tenant schema not in storage but we have schoolId, try to fetch school to get tenant schema
        try {
          const schoolResponse = await axios.get(`${process.env.REACT_APP_API_URL}/schools/${userData.schoolId}`);
          schoolData = schoolResponse.data;
          const schoolTenantSchema = schoolData?.tenant?.schemaName;
          if (schoolTenantSchema) {
            axios.defaults.headers.common['X-Tenant-ID'] = schoolTenantSchema;
            setTenantSchema(schoolTenantSchema);
          }
        } catch (error) {
        }
      }

      // Persist current user info as studentData fallback (used for name display)
      setStudentData({
        studentName: userData.username || '',
        name: userData.username || '',
        email: userData.email || '',
      });

      // Note: Student data fetching with sessionId and classId is handled by useCurrentStudent hook
      // which is called in Layout component after login when session and class context are available

      setUser(userData as User);

      // Prefetch school by ID at first priority for schoolAdmin, teacher, student, and APP_USER roles
      const shouldPrefetchSchool = userData.schoolId && (
        userData.roles?.includes('SCHOOL_ADMIN') ||
        userData.roles?.includes('TEACHER') ||
        userData.roles?.includes('STUDENT') ||
        userData.roles?.includes('APP_USER')
      );

      if (shouldPrefetchSchool) {
        // If we already fetched school data for tenant schema, use it to populate cache
        // Otherwise, prefetch school data immediately with high priority
        if (schoolData) {
          queryClient.setQueryData(['schoolById', userData.schoolId], schoolData);
        } else {
          queryClient.prefetchQuery({
            queryKey: ['schoolById', userData.schoolId],
            queryFn: async () => {
              const response = await axios.get(`${process.env.REACT_APP_API_URL}/schools/${userData.schoolId}`);
              return response.data;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
        }
      }

      return userData as User;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData: { username: string; password: string; email: string; captcha?: string }) => {
    try {
      const response = await authService.register(userData);
      // Remove both 'token' and duplicate 'role' field
      const { token, role, ...userInfo } = response.data;
      
      // Clear any previous student data to avoid showing stale names from past logins
      clearStudentData();

      // Store token and user data (uses sessionStorage - clears when tab closes)
      setAuthToken(token);
      setUserData(JSON.stringify(userInfo));

      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Persist current user info as studentData fallback (used for name display)
      setStudentData({
        studentName: userInfo.username || '',
        name: userInfo.username || '',
        email: userInfo.email || '',
      });

      setUser(userInfo as User);

      // Prefetch school by ID at first priority for student registration (APP_STUDENT role)
      // Also check for other roles that might register
      const shouldPrefetchSchool = userInfo.schoolId && (
        userInfo.roles?.includes('STUDENT') ||
        userInfo.roles?.includes('APP_STUDENT') ||
        userInfo.roles?.includes('SCHOOL_ADMIN') ||
        userInfo.roles?.includes('TEACHER') ||
        userInfo.roles?.includes('APP_USER')
      );

      return userInfo as User;
    } catch (error) {
      throw error;
    }
  };

  const hasRole = (role: string): boolean => {
    return !!user && Array.isArray(user.roles) && user.roles.includes(role);
  };

  const isAdmin = (): boolean => hasRole('ADMIN');
  const isSchoolAdmin = (): boolean => hasRole('SCHOOL_ADMIN');
  const isTeacher = (): boolean => hasRole('TEACHER');
  const isStudent = (): boolean => hasRole('STUDENT');

  const isSchoolAdminUser = isSchoolAdmin();
  // Disable API call for school admin - they should have schoolId from login
  const shouldFetchAdminSchoolId = false; // Disabled for school admin
  const adminSchoolIdQuery = useSchoolIdForAdmin(undefined);
  const effectiveSchoolId = user?.schoolId ?? adminSchoolIdQuery.data ?? null;

  const handleForceLogout = useCallback(() => {
    setWasAutoLoggedOut(true);
    logout();
  }, [logout]);

  useEffect(() => {
    if (!user || !isSchoolAdminUser) {
      disableForceLogoutGuard();
      return;
    }

    enableForceLogoutGuard({
      userId: user.id,
      schoolId: effectiveSchoolId,
      logout: handleForceLogout,
      pollIntervalMs: 30000, // 30 seconds - check admin assignment every 30 seconds to detect changes quickly
    });

    return () => {
      disableForceLogoutGuard();
    };
  }, [user, isSchoolAdminUser, effectiveSchoolId, handleForceLogout]);

  const forgotPassword = async (email: string): Promise<string> => {
    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (email: string, otp: number): Promise<string> => {
    try {
      const response = await authService.verifyOtp(email, otp);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string, newPassword: string): Promise<string> => {
    try {
      const response = await authService.resetPassword(email, newPassword);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        wasAutoLoggedOut,
        login,
        register,
        forgotPassword,
        verifyOtp,
        resetPassword,
        logout,
        clearAutoLogoutFlag,
        isAdmin,
        isSchoolAdmin,
        isTeacher,
        isStudent
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};